import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Radio,
  Grid,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import AdminNavbar from './AdminNavbar';
import './AdminModern.css';

const UserForm = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [csvData, setCsvData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [invitationSearchText, setInvitationSearchText] = useState('');
  const [companyText, setCompanyText] = useState('');
  const [matches, setMatches] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [form, setForm] = useState({ participantName: '', supplierName: '', attend: '', remark: '', guestCount: 1 });
  const [guestForm, setGuestForm] = useState({ participantName: '', supplierName: '', guestCount: 1, remark: '' });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFromCSV, setIsFromCSV] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [checkingSubmission, setCheckingSubmission] = useState(false);
  const [submittedEntries, setSubmittedEntries] = useState(new Set());

  const checkExistingSubmission = useCallback(async (participantName, supplierName) => {
    if (!participantName) {
      setAlreadySubmitted(false);
      setCheckingSubmission(false);
      return;
    }
    
    setCheckingSubmission(true);
    try {
      // const res = await fetch(`https://apitoyotaevent.autostream.lk/api/check-submission/${encodeURIComponent(participantName.trim())}/${encodeURIComponent(supplierName.trim())}`);
      const apiBase = process.env.REACT_APP_API_URL || '';
      const trimmedSupplierName = supplierName ? supplierName.trim() : '';
      const url = trimmedSupplierName 
        ? (apiBase.endsWith('/')
            ? `${apiBase}check-submission/${encodeURIComponent(participantName.trim())}/${encodeURIComponent(trimmedSupplierName)}`
            : `${apiBase}/check-submission/${encodeURIComponent(participantName.trim())}/${encodeURIComponent(trimmedSupplierName)}`)
        : (apiBase.endsWith('/')
            ? `${apiBase}check-submission/${encodeURIComponent(participantName.trim())}`
            : `${apiBase}/check-submission/${encodeURIComponent(participantName.trim())}`);
      const res = await fetch(url);
      const data = await res.json();
      console.log('Check submission result:', data);
      setAlreadySubmitted(data.alreadySubmitted);
    } catch (err) {
      console.error('Error checking submission:', err);
      setAlreadySubmitted(false);
    } finally {
      setCheckingSubmission(false);
    }
  }, []);

  const checkExistingGuestSubmission = useCallback(async (participantName, supplierName) => {
    if (!participantName) {
      setAlreadySubmitted(false);
      setCheckingSubmission(false);
      return;
    }
    
    setCheckingSubmission(true);
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const trimmedSupplierName = supplierName ? supplierName.trim() : '';
      const url = trimmedSupplierName 
        ? (apiBase.endsWith('/')
            ? `${apiBase}check-guest-submission/${encodeURIComponent(participantName.trim())}/${encodeURIComponent(trimmedSupplierName)}`
            : `${apiBase}/check-guest-submission/${encodeURIComponent(participantName.trim())}/${encodeURIComponent(trimmedSupplierName)}`)
        : (apiBase.endsWith('/')
            ? `${apiBase}check-guest-submission/${encodeURIComponent(participantName.trim())}`
            : `${apiBase}/check-guest-submission/${encodeURIComponent(participantName.trim())}`);
      const res = await fetch(url);
      const data = await res.json();
      console.log('Check guest submission result:', data);
      setAlreadySubmitted(data.alreadySubmitted);
    } catch (err) {
      console.error('Error checking guest submission:', err);
      setAlreadySubmitted(false);
    } finally {
      setCheckingSubmission(false);
    }
  }, []);

  useEffect(() => {
      fetchCSVData();
      fetchSubmittedEntries();
    }, [selectedRow]);

  useEffect(() => {
    // Filter matches as user types in Name, Invitation Number, or Company
    if (searchText.trim() === '' && invitationSearchText.trim() === '' && companyText.trim() === '') {
      setMatches([]);
      setSelectedRow(null);
      return;
    }
    
    const filtered = csvData.filter(row => {
      const nameMatch = row['NAME  OF PARTICIPANTS'] &&
        row['NAME  OF PARTICIPANTS'].toLowerCase().includes(searchText.toLowerCase());
      
      // For invitation number, use exact match if the search text is complete
      let invitationMatch = false;
      if (row['INVITATION NO'] && invitationSearchText.trim() !== '') {
        const invitationNo = row['INVITATION NO'].toLowerCase().trim();
        const searchInvitation = invitationSearchText.toLowerCase().trim();
        
        // If search text is complete (no partial match), use exact match
        if (invitationNo === searchInvitation) {
          invitationMatch = true;
        } else {
          // Otherwise use partial match
          invitationMatch = invitationNo.includes(searchInvitation);
        }
      }
      
      // For company name, use partial match
      const companyMatch = row['SUPPLIER NAME'] &&
        row['SUPPLIER NAME'].toLowerCase().includes(companyText.toLowerCase());
      
      // Count how many search fields have content
      const hasNameSearch = searchText.trim() !== '';
      const hasInvitationSearch = invitationSearchText.trim() !== '';
      const hasCompanySearch = companyText.trim() !== '';
      const searchFieldCount = [hasNameSearch, hasInvitationSearch, hasCompanySearch].filter(Boolean).length;
      
      // If all three fields have content, require all to match
      if (hasNameSearch && hasInvitationSearch && hasCompanySearch) {
        return nameMatch && invitationMatch && companyMatch;
      }
      // If two fields have content, require both to match
      else if (searchFieldCount === 2) {
        if (hasNameSearch && hasInvitationSearch) {
          return nameMatch && invitationMatch;
        } else if (hasNameSearch && hasCompanySearch) {
          return nameMatch && companyMatch;
        } else if (hasInvitationSearch && hasCompanySearch) {
          return invitationMatch && companyMatch;
        }
      }
      // If only one field has content, match by that field
      else if (hasNameSearch) {
        return nameMatch;
      } else if (hasInvitationSearch) {
        return invitationMatch;
      } else if (hasCompanySearch) {
        return companyMatch;
      }
      
      return false;
    });
    
    setMatches(filtered);
    // If the selected row is not in the filtered list, clear selection
    if (!filtered.some(row => selectedRow && row.id === selectedRow.id)) {
      setSelectedRow(null);
    }
  }, [searchText, invitationSearchText, companyText, csvData, selectedRow]);

  useEffect(() => {
    // When a row is selected, update the fields
    if (selectedRow) {
      if (activeTab === 0) {
        setForm({
          participantName: selectedRow['NAME  OF PARTICIPANTS'] || '',
          supplierName: selectedRow['SUPPLIER NAME'] || '',
          attend: '',
          remark: '',
          guestCount: 1
        });
      } else {
        setGuestForm({
          participantName: selectedRow['NAME  OF PARTICIPANTS'] || '',
          supplierName: selectedRow['SUPPLIER NAME'] || '',
          guestCount: 1,
          remark: ''
        });
      }
      setCompanyText(selectedRow['SUPPLIER NAME'] || '');
      setIsFromCSV(true);
      if (activeTab === 0) {
        checkExistingSubmission(selectedRow['NAME  OF PARTICIPANTS'] || '', selectedRow['SUPPLIER NAME'] || '');
      } else {
        checkExistingGuestSubmission(selectedRow['NAME  OF PARTICIPANTS'] || '', selectedRow['SUPPLIER NAME'] || '');
      }
    } else {
      setIsFromCSV(false);
      setAlreadySubmitted(false);
    }
  }, [selectedRow, activeTab, checkExistingSubmission, checkExistingGuestSubmission]);

  useEffect(() => {
    // If user types in Name field, update form and clear selection (only for RSVP tab)
    if (activeTab === 0) {
      setForm(prev => ({ ...prev, participantName: searchText }));
      if (!selectedRow || searchText !== (selectedRow['NAME  OF PARTICIPANTS'] || '')) {
        setSelectedRow(null);
        setIsFromCSV(false);
        setAlreadySubmitted(false);
      }
    }
  }, [searchText, activeTab, selectedRow]);

  useEffect(() => {
    // If user types in Invitation Number field, clear selection if it doesn't match selected row
    if (activeTab === 0) {
      if (!selectedRow || invitationSearchText !== (selectedRow['INVITATION NO'] || '')) {
        setSelectedRow(null);
        setIsFromCSV(false);
        setAlreadySubmitted(false);
      }
    }
  }, [invitationSearchText, activeTab, selectedRow]);

  useEffect(() => {
    // If user types in Company field, update form (only for RSVP tab)
    if (activeTab === 0) {
      setForm(prev => ({ ...prev, supplierName: companyText }));
      // Check for existing submission when participant name is filled
      if (form.participantName && !isFromCSV) {
        checkExistingSubmission(form.participantName, companyText);
      }
      // Clear selection if company text doesn't match selected row
      if (!selectedRow || companyText !== (selectedRow['SUPPLIER NAME'] || '')) {
        setSelectedRow(null);
        setIsFromCSV(false);
        setAlreadySubmitted(false);
      }
    }
  }, [companyText, form.participantName, isFromCSV, activeTab, selectedRow, checkExistingSubmission]);

  // Check for existing guest submission when guest form fields are filled
  useEffect(() => {
    if (activeTab === 1 && guestForm.participantName) {
      checkExistingGuestSubmission(guestForm.participantName, guestForm.supplierName);
    } else if (activeTab === 1) {
      setAlreadySubmitted(false);
      setCheckingSubmission(false);
    }
  }, [guestForm.participantName, guestForm.supplierName, activeTab, checkExistingGuestSubmission]);

  const fetchCSVData = async () => {
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const csvUrl = apiBase.endsWith('/') ? apiBase + 'csv-data' : apiBase + '/csv-data';
      const res = await fetch(csvUrl);
      const data = await res.json();
      setCsvData(data);
    } catch (err) {
      setStatus({ type: 'error', message: 'Failed to load participant data.' });
    }
  };

  const fetchSubmittedEntries = async () => {
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const entriesUrl = apiBase.endsWith('/') ? apiBase + 'submitted-entries' : apiBase + '/submitted-entries';
      const res = await fetch(entriesUrl);
      const data = await res.json();
      const submittedSet = new Set();
      data.submissions.forEach(submission => {
        const key = `${submission.participant_name.toLowerCase().trim()}-${submission.supplier_name.toLowerCase().trim()}`;
        submittedSet.add(key);
      });
      setSubmittedEntries(submittedSet);
    } catch (err) {
      console.error('Error fetching submitted entries:', err);
    }
  };

  const isEntrySubmitted = (participantName, supplierName) => {
    const key = `${participantName.toLowerCase().trim()}-${supplierName.toLowerCase().trim()}`;
    return submittedEntries.has(key);
  };

  const handleSelectRow = (row) => {
    setSelectedRow(row);
    setSearchText(row['NAME  OF PARTICIPANTS'] || '');
    setInvitationSearchText(row['INVITATION NO'] || '');
    setCompanyText(row['SUPPLIER NAME'] || '');
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setStatus(null);
    setAlreadySubmitted(false);
    setCheckingSubmission(false);
    setSelectedRow(null);
    setSearchText('');
    setInvitationSearchText('');
    setCompanyText('');
    if (newValue === 0) {
      setForm({ participantName: '', supplierName: '', attend: '', remark: '', guestCount: 1 });
    } else {
      setGuestForm({ participantName: '', supplierName: '', guestCount: 1, remark: '' });
      // Clear any existing submission status for guest tab
      setAlreadySubmitted(false);
      setCheckingSubmission(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    
    const trimmedParticipantName = form.participantName.trim();
    const trimmedSupplierName = form.supplierName.trim();
    
    if (!trimmedParticipantName) {
      setStatus({ type: 'error', message: 'Please enter your name.' });
      setLoading(false);
      return;
    }
    
    // Check if a row is selected from CSV data (only allow submissions from existing data)
    if (!selectedRow) {
      setStatus({ type: 'error', message: 'Please search and select your name from the database. Only existing participants can submit RSVP.' });
      setLoading(false);
      return;
    }
    
    // Double-check for existing submission before submitting
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const checkUrl = apiBase.endsWith('/')
        ? `${apiBase}check-submission/${encodeURIComponent(trimmedParticipantName)}/${encodeURIComponent(trimmedSupplierName)}`
        : `${apiBase}/check-submission/${encodeURIComponent(trimmedParticipantName)}/${encodeURIComponent(trimmedSupplierName)}`;
      const checkRes = await fetch(checkUrl);
      const checkData = await checkRes.json();
      
      if (checkData.alreadySubmitted) {
        setAlreadySubmitted(true);
        setStatus({ type: 'error', message: 'You have already submitted this form. Duplicate submissions are not allowed.' });
        setLoading(false);
        return;
      }
    } catch (err) {
      console.error('Error checking submission before submit:', err);
    }
    
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const submitUrl = apiBase.endsWith('/') ? apiBase + 'user-form-submit' : apiBase + '/user-form-submit';
      const res = await fetch(submitUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          participantName: trimmedParticipantName,
          supplierName: trimmedSupplierName,
          guestCount: form.guestCount,
          remark: form.remark
        }),
      });
      const data = await res.json();
      if (data.success) {
        setStatus({ type: 'success', message: 'Form submitted successfully!' });
        setForm({ participantName: '', supplierName: '', attend: '', remark: '', guestCount: 1 });
        setSearchText('');
        setCompanyText('');
        setSelectedRow(null);
        setIsFromCSV(false);
        setAlreadySubmitted(false);
        
        // Update the submittedEntries state to include the newly submitted entry
        const newKey = `${trimmedParticipantName.toLowerCase().trim()}-${trimmedSupplierName.toLowerCase().trim()}`;
        setSubmittedEntries(prev => new Set([...prev, newKey]));
      } else {
        if (data.alreadySubmitted) {
          setAlreadySubmitted(true);
          setStatus({ type: 'error', message: data.error || 'You have already submitted this form.' });
        } else {
          setStatus({ type: 'error', message: data.error || 'Submission failed.' });
        }
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Network or server error.' });
    }
    setLoading(false);
  };

  const handleGuestSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    
    const trimmedParticipantName = guestForm.participantName.trim();
    const trimmedSupplierName = guestForm.supplierName.trim();
    
    if (!trimmedParticipantName) {
      setStatus({ type: 'error', message: 'Please enter participant name.' });
      setLoading(false);
      return;
    }
    
    // Double-check for existing submission before submitting
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const checkGuestUrl = apiBase.endsWith('/')
        ? `${apiBase}check-guest-submission/${encodeURIComponent(trimmedParticipantName)}/${encodeURIComponent(trimmedSupplierName)}`
        : `${apiBase}/check-guest-submission/${encodeURIComponent(trimmedParticipantName)}/${encodeURIComponent(trimmedSupplierName)}`;
      const checkRes = await fetch(checkGuestUrl);
      const checkData = await checkRes.json();
      
      if (checkData.alreadySubmitted) {
        setAlreadySubmitted(true);
        setStatus({ type: 'error', message: 'You have already submitted a guest registration for this participant and supplier. Duplicate submissions are not allowed.' });
        setLoading(false);
        return;
      }
    } catch (err) {
      console.error('Error checking guest submission before submit:', err);
    }
    
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const guestSubmitUrl = apiBase.endsWith('/') ? apiBase + 'guest-registration-submit' : apiBase + '/guest-registration-submit';
      const res = await fetch(guestSubmitUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          participantName: trimmedParticipantName,
          supplierName: trimmedSupplierName,
          guestCount: guestForm.guestCount,
          remark: guestForm.remark
        }),
      });
      const data = await res.json();
      if (data.success) {
        setStatus({ type: 'success', message: 'Guest registration submitted successfully!' });
        setGuestForm({ participantName: '', supplierName: '', guestCount: 1, remark: '' });
        setAlreadySubmitted(false);
        
      } else {
        if (data.alreadySubmitted) {
          setAlreadySubmitted(true);
          setStatus({ type: 'error', message: data.error || 'You have already submitted a guest registration for this participant and supplier.' });
        } else {
          setStatus({ type: 'error', message: data.error || 'Submission failed.' });
        }
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Network or server error.' });
    }
    setLoading(false);
  };

  return (
    <div className="admin-page-wrapper">
      <AdminNavbar />
      <div className="admin-bg"></div>
      <div className="admin-main-container">
        <Box className="admin-glass-card no-hover" sx={{ 
          maxWidth: { xs: '95%', sm: 600 }, 
          mx: 'auto', 
          p: { xs: 2, sm: 4 }, 
          borderRadius: 3, 
          boxShadow: 3 
        }}>
          <Typography variant="h4" align="center" fontWeight={700} color="primary" sx={{ 
            mb: 2, 
            letterSpacing: 1, 
            color: '#b00',
            fontSize: { xs: '1.5rem', sm: '2.125rem' }
          }}>
            Event Registration
          </Typography>
          
          <Tabs value={activeTab} onChange={handleTabChange} centered sx={{ mb: 3 }}>
            <Tab label="RSVP Registration" />
            <Tab label="Guest Registration" />
          </Tabs>

          {activeTab === 0 ? (
            // RSVP Registration Tab
            <>
              <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
                Please search for your name, invitation number, or company from the database. Only existing participants can submit RSVP.
                {searchText.trim() !== '' && invitationSearchText.trim() !== '' && companyText.trim() !== '' && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Showing results that match name, invitation number, and company
                  </Alert>
                )}
                {searchText.trim() !== '' && invitationSearchText.trim() !== '' && companyText.trim() === '' && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Showing results that match both name and invitation number
                  </Alert>
                )}
                {searchText.trim() !== '' && invitationSearchText.trim() === '' && companyText.trim() !== '' && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Showing results that match both name and company
                  </Alert>
                )}
                {searchText.trim() === '' && invitationSearchText.trim() !== '' && companyText.trim() !== '' && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Showing results that match both invitation number and company
                  </Alert>
                )}
                {searchText.trim() !== '' && invitationSearchText.trim() === '' && companyText.trim() === '' && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Showing results that match the name
                  </Alert>
                )}
                {searchText.trim() === '' && invitationSearchText.trim() !== '' && companyText.trim() === '' && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Showing results that match the invitation number
                  </Alert>
                )}
                {searchText.trim() === '' && invitationSearchText.trim() === '' && companyText.trim() !== '' && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Showing results that match the company
                  </Alert>
                )}
                {checkingSubmission && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Checking for existing submissions...
                  </Alert>
                )}
                {alreadySubmitted && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    This participant has already submitted a form.
                  </Alert>
                )}
              </Typography>
              
              <form onSubmit={handleSubmit} autoComplete="off">
                <Grid container spacing={{ xs: 1, sm: 2 }}>
                  <Grid item xs={12}>
                    <TextField
                      label="Name"
                      value={searchText}
                      onChange={e => setSearchText(e.target.value)}
                      fullWidth
                      autoComplete="off"
                      placeholder="Search for your name in the database"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: alreadySubmitted ? '#f44336' : (selectedRow ? '#2e7d32' : '#b00'),
                          },
                          '&:hover fieldset': {
                            borderColor: alreadySubmitted ? '#d32f2f' : (selectedRow ? '#388e3c' : '#d00'),
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: alreadySubmitted ? '#f44336' : (selectedRow ? '#2e7d32' : '#b00'),
                          },
                          backgroundColor: alreadySubmitted ? 'rgba(244, 67, 54, 0.05)' : (selectedRow ? 'rgba(76, 175, 80, 0.05)' : 'transparent'),
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Invitation Number"
                      value={invitationSearchText}
                      onChange={e => setInvitationSearchText(e.target.value)}
                      fullWidth
                      autoComplete="off"
                      placeholder="Search by invitation number"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: alreadySubmitted ? '#f44336' : (selectedRow ? '#2e7d32' : '#b00'),
                          },
                          '&:hover fieldset': {
                            borderColor: alreadySubmitted ? '#d32f2f' : (selectedRow ? '#388e3c' : '#d00'),
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: alreadySubmitted ? '#f44336' : (selectedRow ? '#2e7d32' : '#b00'),
                          },
                          backgroundColor: alreadySubmitted ? 'rgba(244, 67, 54, 0.05)' : (selectedRow ? 'rgba(76, 175, 80, 0.05)' : 'transparent'),
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Company"
                      value={companyText}
                      onChange={e => setCompanyText(e.target.value)}
                      fullWidth
                      autoComplete="off"
                      placeholder="Search for your company in the database"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: alreadySubmitted ? '#f44336' : (selectedRow ? '#2e7d32' : '#b00'),
                          },
                          '&:hover fieldset': {
                            borderColor: alreadySubmitted ? '#d32f2f' : (selectedRow ? '#388e3c' : '#d00'),
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: alreadySubmitted ? '#f44336' : (selectedRow ? '#2e7d32' : '#b00'),
                          },
                          backgroundColor: alreadySubmitted ? 'rgba(244, 67, 54, 0.05)' : (selectedRow ? 'rgba(76, 175, 80, 0.05)' : 'transparent'),
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Guest Count"
                      type="number"
                      value={form.guestCount}
                      onChange={e => setForm(prev => ({ ...prev, guestCount: parseInt(e.target.value) || 1 }))}
                      fullWidth
                      inputProps={{ min: 1, max: 3 }}
                      autoComplete="off"
                      placeholder="Number of guests (1-3)"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: alreadySubmitted ? '#f44336' : (selectedRow ? '#2e7d32' : '#b00'),
                          },
                          '&:hover fieldset': {
                            borderColor: alreadySubmitted ? '#d32f2f' : (selectedRow ? '#388e3c' : '#d00'),
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: alreadySubmitted ? '#f44336' : (selectedRow ? '#2e7d32' : '#b00'),
                          },
                          backgroundColor: alreadySubmitted ? 'rgba(244, 67, 54, 0.05)' : (selectedRow ? 'rgba(76, 175, 80, 0.05)' : 'transparent'),
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Remark"
                      value={form.remark}
                      onChange={e => setForm(prev => ({ ...prev, remark: e.target.value }))}
                      fullWidth
                      multiline
                      rows={1}
                      autoComplete="off"
                      placeholder="Enter any additional remarks or comments (optional)"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: alreadySubmitted ? '#f44336' : (selectedRow ? '#2e7d32' : '#b00'),
                          },
                          '&:hover fieldset': {
                            borderColor: alreadySubmitted ? '#d32f2f' : (selectedRow ? '#388e3c' : '#d00'),
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: alreadySubmitted ? '#f44336' : (selectedRow ? '#2e7d32' : '#b00'),
                          },
                          backgroundColor: alreadySubmitted ? 'rgba(244, 67, 54, 0.05)' : (selectedRow ? 'rgba(76, 175, 80, 0.05)' : 'transparent'),
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                {/* Table of matches */}
                {matches.length > 0 && (
                  <TableContainer component={Paper} sx={{ 
                    mt: { xs: 2, sm: 3 }, 
                    mb: { xs: 1, sm: 2 }, 
                    borderRadius: 2, 
                    background: 'rgba(255,255,255,0.97)',
                    overflowX: 'auto'
                  }}>
                    <Table size="small" className="admin-table" sx={{ minWidth: { xs: 300, sm: 400 } }}>
                                              <TableHead>
                          <TableRow>
                            <TableCell sx={{ 
                              color: '#b00', 
                              fontWeight: 700,
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              padding: { xs: 1, sm: 2 }
                            }}>Name</TableCell>
                            <TableCell sx={{ 
                              color: '#b00', 
                              fontWeight: 700,
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              padding: { xs: 1, sm: 2 }
                            }}>Invitation No.</TableCell>
                            <TableCell sx={{ 
                              color: '#b00', 
                              fontWeight: 700,
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              padding: { xs: 1, sm: 2 }
                            }}>Company</TableCell>
                            <TableCell align="center" sx={{ 
                              color: '#b00', 
                              fontWeight: 700,
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              padding: { xs: 1, sm: 2 }
                            }}>Select</TableCell>
                          </TableRow>
                        </TableHead>
                      <TableBody>
                        {matches.map((row, idx) => {
                          const isSubmitted = isEntrySubmitted(row['NAME  OF PARTICIPANTS'], row['SUPPLIER NAME']);
                          return (
                            <TableRow 
                              key={row.id} 
                              className="fade-in-row" 
                              style={{ 
                                animationDelay: `${idx * 40}ms`,
                                backgroundColor: isSubmitted ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.05)',
                                borderLeft: `4px solid ${isSubmitted ? '#f44336' : '#2e7d32'}`
                              }}
                            >
                              <TableCell sx={{ 
                                color: isSubmitted ? '#f44336' : '#2e7d32', 
                                fontWeight: 500,
                                textDecoration: isSubmitted ? 'line-through' : 'none',
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                padding: { xs: 1, sm: 2 },
                                wordBreak: 'break-word'
                              }}>
                                {row['NAME  OF PARTICIPANTS']}
                                {isSubmitted && (
                                  <Typography variant="caption" sx={{ 
                                    ml: 1, 
                                    color: '#f44336', 
                                    fontWeight: 600,
                                    fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                  }}>
                                    (Already Submitted)
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ 
                                color: isSubmitted ? '#f44336' : '#2e7d32', 
                                fontWeight: 500,
                                textDecoration: isSubmitted ? 'line-through' : 'none',
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                padding: { xs: 1, sm: 2 },
                                wordBreak: 'break-word'
                              }}>
                                {row['INVITATION NO']}
                              </TableCell>
                              <TableCell sx={{ 
                                color: isSubmitted ? '#f44336' : '#2e7d32', 
                                fontWeight: 500,
                                textDecoration: isSubmitted ? 'line-through' : 'none',
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                padding: { xs: 1, sm: 2 },
                                wordBreak: 'break-word'
                              }}>
                                {row['SUPPLIER NAME']}
                              </TableCell>
                              <TableCell align="center">
                                <Radio
                                  checked={selectedRow && selectedRow.id === row.id}
                                  onChange={() => handleSelectRow(row)}
                                  value={row.id}
                                  inputProps={{ 'aria-label': row['NAME  OF PARTICIPANTS'] }}
                                  sx={{
                                    color: isSubmitted ? '#f44336' : '#2e7d32',
                                    '&.Mui-checked': {
                                      color: isSubmitted ? '#f44336' : '#2e7d32',
                                    },
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  className="admin-btn"
                  fullWidth
                  sx={{ 
                    mt: { xs: 1, sm: 2 }, 
                    fontWeight: 600, 
                    letterSpacing: 1, 
                    boxShadow: 2, 
                    py: { xs: 1, sm: 1.2 }, 
                    fontSize: { xs: 16, sm: 18 }, 
                    borderRadius: 2, 
                    transition: 'background 0.3s',
                    backgroundColor: alreadySubmitted ? '#f57c00' : (selectedRow ? '#2e7d32' : '#cccccc'),
                    '&:hover': {
                      backgroundColor: alreadySubmitted ? '#ef6c00' : (selectedRow ? '#388e3c' : '#cccccc'),
                    }
                  }}
                  disabled={loading || alreadySubmitted || checkingSubmission || !selectedRow}
                >
                  {loading ? 'Submitting...' : checkingSubmission ? 'Checking...' : alreadySubmitted ? 'Already Submitted' : !selectedRow ? 'Select from Database' : 'Confirm RSVP'}
                </Button>
              </form>
            </>
          ) : (
            // Guest Registration Tab
            <>
              <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
                Register additional guests for the event. Please enter new guest details below.
                {checkingSubmission && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Checking for existing guest registrations...
                  </Alert>
                )}
                {alreadySubmitted && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    This participant has already submitted a guest registration.
                  </Alert>
                )}
              </Typography>
              
              <form onSubmit={handleGuestSubmit} autoComplete="off">
                <Grid container spacing={{ xs: 1, sm: 2 }}>
                  <Grid item xs={12}>
                    <TextField
                      label="Participant Name"
                      value={guestForm.participantName}
                      onChange={e => setGuestForm(prev => ({ ...prev, participantName: e.target.value }))}
                      fullWidth
                      autoComplete="off"
                      placeholder="Enter participant name"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: alreadySubmitted ? '#f44336' : '#b00',
                          },
                          '&:hover fieldset': {
                            borderColor: alreadySubmitted ? '#d32f2f' : '#d00',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: alreadySubmitted ? '#f44336' : '#b00',
                          },
                          backgroundColor: alreadySubmitted ? 'rgba(244, 67, 54, 0.05)' : 'transparent',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Company Name (Optional)"
                      value={guestForm.supplierName}
                      onChange={e => setGuestForm(prev => ({ ...prev, supplierName: e.target.value }))}
                      fullWidth
                      autoComplete="off"
                      placeholder="Enter company name (optional)"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: alreadySubmitted ? '#f44336' : '#b00',
                          },
                          '&:hover fieldset': {
                            borderColor: alreadySubmitted ? '#d32f2f' : '#d00',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: alreadySubmitted ? '#f44336' : '#b00',
                          },
                          backgroundColor: alreadySubmitted ? 'rgba(244, 67, 54, 0.05)' : 'transparent',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Guest Count"
                      type="number"
                      value={guestForm.guestCount}
                      onChange={e => setGuestForm(prev => ({ ...prev, guestCount: parseInt(e.target.value) || 1 }))}
                      fullWidth
                      inputProps={{ min: 1, max: 5 }}
                      autoComplete="off"
                      placeholder="Number of additional guests (1-5)"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: alreadySubmitted ? '#f44336' : '#b00',
                          },
                          '&:hover fieldset': {
                            borderColor: alreadySubmitted ? '#d32f2f' : '#d00',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: alreadySubmitted ? '#f44336' : '#b00',
                          },
                          backgroundColor: alreadySubmitted ? 'rgba(244, 67, 54, 0.05)' : 'transparent',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Remark"
                      value={guestForm.remark}
                      onChange={e => setGuestForm(prev => ({ ...prev, remark: e.target.value }))}
                      fullWidth
                      multiline
                      rows={1}
                      autoComplete="off"
                      placeholder="Enter any additional remarks or comments (optional)"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: alreadySubmitted ? '#f44336' : '#b00',
                          },
                          '&:hover fieldset': {
                            borderColor: alreadySubmitted ? '#d32f2f' : '#d00',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: alreadySubmitted ? '#f44336' : '#b00',
                          },
                          backgroundColor: alreadySubmitted ? 'rgba(244, 67, 54, 0.05)' : 'transparent',
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                <Button
                  type="submit"
                  variant="contained"
                  className="admin-btn"
                  fullWidth
                  sx={{ 
                    mt: { xs: 1, sm: 2 }, 
                    fontWeight: 600, 
                    letterSpacing: 1, 
                    boxShadow: 2, 
                    py: { xs: 1, sm: 1.2 }, 
                    fontSize: { xs: 16, sm: 18 }, 
                    borderRadius: 2, 
                    transition: 'background 0.3s',
                    backgroundColor: alreadySubmitted ? '#f57c00' : '#b00',
                    '&:hover': {
                      backgroundColor: alreadySubmitted ? '#ef6c00' : '#d00',
                    }
                  }}
                  disabled={loading || alreadySubmitted || checkingSubmission}
                >
                  {loading ? 'Submitting...' : checkingSubmission ? 'Checking...' : alreadySubmitted ? 'Already Registered' : 'Register Guests'}
                </Button>
              </form>
            </>
          )}
          
          {status && (
            <Alert severity={status.type} sx={{ mt: 2 }}>
              {status.message}
            </Alert>
          )}
        </Box>
      </div>
      <style>{`
        .fade-in-row {
          animation: fadeInUp 0.5s both;
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: none; }
        }
        
        /* Fix for UserForm specific positioning */
        .admin-page-wrapper {
          padding-top: 80px;
        }
        
        .admin-main-container {
          margin-top: 20px;
          position: relative;
          z-index: 1;
        }
        
        /* Ensure the glass card doesn't move on hover */
        .admin-glass-card.no-hover {
          transform: none !important;
          transition: none !important;
        }
        
        /* Fix table positioning */
        .admin-table {
          position: relative;
          z-index: 2;
        }
        
        /* CSV data styling */
        .csv-data-row {
          background-color: rgba(76, 175, 80, 0.05) !important;
          border-left: 4px solid #2e7d32 !important;
        }
        
        .csv-data-row:hover {
          background-color: rgba(76, 175, 80, 0.1) !important;
        }
        
        /* Manual entry styling */
        .manual-entry {
          background-color: rgba(176, 0, 0, 0.05) !important;
          border-left: 4px solid #b00 !important;
        }
        
        .manual-entry:hover {
          background-color: rgba(176, 0, 0, 0.1) !important;
        }
        
        /* Mobile responsive styles */
        @media (max-width: 600px) {
          .admin-page-wrapper {
            padding-top: 60px;
          }
          
          .admin-main-container {
            margin-top: 10px;
            padding: 0 10px;
          }
          
          .admin-glass-card.no-hover {
            margin: 0 10px;
          }
        }
        
        /* MacBook and tablet responsive styles */
        @media (min-width: 601px) and (max-width: 1024px) {
          .admin-main-container {
            padding: 0 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default UserForm; 